import { useAuth } from '@/auth/useAuth';
import { Button, Card, CardBody, LoadingContainer, Modal, Textarea } from '@/components/ui';
import { useEmpresa } from '@/hooks/useEmpresas';
import { useIniciarAsesoria } from '@/hooks/useIniciarAsesoria';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function CrearAsesoriaPage() {
  const { nit } = useParams<{ nit: string }>();
  const navigate = useNavigate();
  const { empresa, isLoading, error: empresaError } = useEmpresa(nit || '');
  const { isAuthenticated } = useAuth();
  const { iniciarAsesoria, isLoading: isSubmitting, error } = useIniciarAsesoria();

  const [objetivo, setObjetivo] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Redirigir si no está autenticado
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) {
    return <LoadingContainer text="Cargando..." />;
  }

  if (empresaError || !empresa) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-700">Empresa no encontrada</h2>
        <Link to="/">
          <Button variant="primary" className="mt-4">
            Volver al inicio
          </Button>
        </Link>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!objetivo.trim()) {
      return;
    }

    try {
      if (!nit) {
        throw new Error('NIT de empresa no definido');
      }
      const resp = await iniciarAsesoria(nit, objetivo.trim());
      const id = resp?.asesoria?.id;
      setShowSuccessModal(true);

      // Redirigir después de 2 segundos a la página de detalle de la asesoría
      setTimeout(() => {
        setShowSuccessModal(false);
        if (id) {
          navigate(`/empresa/${nit}/asesoria/${id}`);
        } else {
          navigate(`/empresa/${nit}`);
        }
      }, 2000);
    } catch (err) {
      console.error('Error al iniciar asesoría:', err);
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className="w-full sm:max-w-160">
        {/* Header */}
        <div className="flex items-center gap-4 py-2 ">
          <Button variant="secondary" size="sm" onClick={() => navigate(`/empresa/${nit}`)}>
            ←
          </Button>
          <div className="text-center flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Iniciar Asesoría</h1>
            <p className="text-gray-500">NIT: {empresa.nit}</p>
            <p className="text-gray-500">{empresa.nombre}</p>
          </div>
        </div>

        {/* Formulario */}
        <Card>
          <CardBody>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg w-full">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <Textarea
                  label="Objetivo de la Asesoría"
                  placeholder="Describe el objetivo principal de esta asesoría..."
                  rows={4}
                  value={objetivo}
                  onChange={(e) => setObjetivo(e.target.value)}
                  required={true}
                />
              </div>

              <Button type="submit" variant="success" fullWidth={true} isLoading={isSubmitting} disabled={!objetivo.trim() || isSubmitting}>
                {isSubmitting ? 'Iniciando asesoría...' : 'Iniciar Asesoría'}
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>

      <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} title="¡Asesoría Iniciada!">
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <title>Checkmark</title>
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">¡Asesoría iniciada correctamente!</h3>
          <p className="text-gray-600 mb-4">Se ha creado la asesoría para {empresa.nombre}</p>
          <p className="text-sm text-gray-500">Redireccionando en unos segundos...</p>
        </div>
      </Modal>
    </div>
  );
}
